import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Paperclip, X } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const QuickAnswerSchema = Yup.object().shape({
	shortcut: Yup.string()
		.min(2, "Muito curto")
		.max(15, "Muito longo")
		.required("Campo obrigatório"),
	message: Yup.string()
		.min(8, "Muito curto")
		.max(30000, "Muito longo")
		.required("Campo obrigatório"),
});

const QuickAnswersModal = ({
	open,
	onClose,
	quickAnswerId,
	initialValues,
	onSave,
}) => {
	const isMounted = useRef(true);

	const initialState = {
		shortcut: "",
		message: "",
	};

	const [quickAnswer, setQuickAnswer] = useState(initialState);
	// Arquivo escolhido agora (ainda não enviado) e o que já está salvo.
	const [arquivo, setArquivo] = useState(null);
	const [midiaSalva, setMidiaSalva] = useState(null);
	const [removerMidia, setRemoverMidia] = useState(false);
	const arquivoRef = useRef(null);

	// Para onde o cartão anda quando esta resposta for enviada. Fica fora do
	// Formik porque não é texto do formulário: é o efeito da resposta, e a
	// tela precisa saber se existe quadro para não mostrar um seletor vazio.
	const [colunas, setColunas] = useState([]);
	const [moveKanbanColumnId, setMoveKanbanColumnId] = useState(null);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open) return;

		const carregarColunas = async () => {
			try {
				const { data } = await api.get("/kanban");
				if (!isMounted.current) return;
				setColunas(data || []);
			} catch (err) {
				// Sem quadro montado não é erro do formulário: a resposta pronta
				// continua servindo, só não move cartão nenhum.
				setColunas([]);
			}
		};

		carregarColunas();
	}, [open]);

	useEffect(() => {
		const fetchQuickAnswer = async () => {
			if (initialValues) {
				setQuickAnswer((prevState) => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!quickAnswerId) return;

			try {
				const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
				if (isMounted.current) {
					setQuickAnswer(data);
					setMidiaSalva(
						data.mediaUrl
							? { url: data.mediaUrl, tipo: data.mediaType, nome: data.mediaName }
							: null
					);
					setArquivo(null);
					setRemoverMidia(false);
					setMoveKanbanColumnId(data.moveKanbanColumnId || null);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchQuickAnswer();
	}, [quickAnswerId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setQuickAnswer(initialState);
		setArquivo(null);
		setMidiaSalva(null);
		setRemoverMidia(false);
		setMoveKanbanColumnId(null);
	};

	const handleSaveQuickAnswer = async (values) => {
		try {
			// multipart sempre: com ou sem arquivo o backend lê os dois iguais,
			// e assim não há dois caminhos de gravação para manter.
			const corpo = new FormData();
			corpo.append("shortcut", values.shortcut);
			corpo.append("message", values.message || "");
			if (arquivo) corpo.append("medias", arquivo);
			if (removerMidia) corpo.append("removerMidia", "true");
			// Sempre mandado, inclusive vazio: é assim que "tirei a coluna"
			// chega ao backend — campo ausente lá significa "não mexi".
			corpo.append("moveKanbanColumnId", moveKanbanColumnId || "");

			if (quickAnswerId) {
				await api.put(`/quickAnswers/${quickAnswerId}`, corpo);
				handleClose();
			} else {
				const { data } = await api.post("/quickAnswers", corpo);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("quickAnswersModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{quickAnswerId ? "Editar resposta pronta" : "Nova resposta pronta"}
					</DialogTitle>
					<DialogDescription>
						Um texto que a equipe repete muito, guardado atrás de um atalho
						curto. Ninguém precisa mais digitar tudo de novo.
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={quickAnswer}
					enableReinitialize={true}
					validationSchema={QuickAnswerSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveQuickAnswer(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ errors, touched, isSubmitting }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="shortcut">
									{i18n.t("quickAnswersModal.form.shortcut")}
								</Label>
								<Field
									as={Input}
									id="shortcut"
									name="shortcut"
									autoFocus
									placeholder="horario"
								/>
								{touched.shortcut && errors.shortcut && (
									<p className="text-xs text-destructive">{errors.shortcut}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									É o que você digita depois da barra, na conversa. Com o atalho{" "}
									<span className="font-medium text-foreground">horario</span>,
									digite{" "}
									<span className="font-medium text-foreground">/horario</span>{" "}
									e a resposta aparece para escolher.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="message">
									{i18n.t("quickAnswersModal.form.message")}
								</Label>
								<Field
									as={Textarea}
									id="message"
									name="message"
									rows={5}
									placeholder="Ex.: Atendemos de segunda a sexta, das 8h às 18h."
								/>
								{touched.message && errors.message && (
									<p className="text-xs text-destructive">{errors.message}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Nada é enviado sozinho: o texto entra no campo de mensagem e a
									pessoa ainda revisa antes de mandar. Com um arquivo anexado,
									este texto vira a legenda dele.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label>
									Arquivo{" "}
									<span className="font-normal text-muted-foreground">
										(opcional)
									</span>
								</Label>

								{/* Já salvo e sem troca pendente: mostra o que está lá. */}
								{midiaSalva && !arquivo && !removerMidia ? (
									<div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2.5">
										<Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
										<span className="min-w-0 flex-1 truncate text-sm">
											{midiaSalva.nome || midiaSalva.url}
										</span>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											aria-label="Remover arquivo"
											title="Remover arquivo"
											onClick={() => setRemoverMidia(true)}
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								) : arquivo ? (
									<div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2.5">
										<Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
										<span className="min-w-0 flex-1 truncate text-sm">
											{arquivo.name}
										</span>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											aria-label="Tirar arquivo escolhido"
											title="Tirar arquivo escolhido"
											onClick={() => {
												setArquivo(null);
												if (arquivoRef.current) arquivoRef.current.value = "";
											}}
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								) : (
									<Button
										type="button"
										variant="outline"
										className="w-full"
										onClick={() => arquivoRef.current?.click()}
									>
										<Paperclip className="h-4 w-4" />
										Escolher imagem, áudio, vídeo ou documento
									</Button>
								)}

								<input
									ref={arquivoRef}
									type="file"
									className="hidden"
									accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
									onChange={(e) => {
										const escolhido = e.target.files?.[0];
										if (!escolhido) return;
										setArquivo(escolhido);
										setRemoverMidia(false);
									}}
								/>

								<p className="text-xs leading-relaxed text-muted-foreground">
									Sai junto com o texto quando alguém usar o atalho — a tabela
									de preços, o catálogo, um áudio pronto.
								</p>
							</div>

							{colunas.length > 0 && (
								<div className="space-y-3 rounded-lg border bg-muted/30 p-3">
									<div>
										<p className="text-sm font-medium">
											Ao enviar, mover o cartão
										</p>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Quando esta resposta for enviada numa conversa, o cartão
											daquele cliente vai para esta coluna do quadro. É como
											avisar "seu pedido entrou em produção" e o Kanban já
											ficar certo, sem passar na outra tela.
										</p>
									</div>

									<div className="space-y-1.5">
										<Label>Coluna do quadro</Label>
										<Select
											value={
												moveKanbanColumnId
													? String(moveKanbanColumnId)
													: "nao"
											}
											onValueChange={(v) =>
												setMoveKanbanColumnId(v === "nao" ? null : Number(v))
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="nao">Não mover o cartão</SelectItem>
												{colunas.map((coluna) => (
													<SelectItem
														key={coluna.id}
														value={String(coluna.id)}
													>
														{coluna.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Move o cartão mais recente do cliente. Se ele ainda não
											tiver cartão nenhum, um é aberto nesta coluna.
										</p>
									</div>
								</div>
							)}

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("quickAnswersModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{quickAnswerId ? "Salvar alterações" : "Criar resposta"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default QuickAnswersModal;
